import * as Yup from 'yup';

/**
 * Har Ghar Tiranga - Har Ghar Swachhata 2026 — ULB reporting format.
 * One submission per ULB covering the whole campaign (9-17 Aug 2026);
 * after submitting, the ULB edits that entry rather than creating another.
 *
 * Field numbering below matches the indicators in the official reporting format.
 */

export const EVENT_TYPE = 'HGT_HGS_2026';
export const MEDIA_TYPE = 'HGT_HGS_2026';

// Indicator 9 asks for 2-3 high-resolution photographs.
export const MIN_PHOTOS = 1;
export const MAX_PHOTOS = 3;

export const MAX_COUNT = 9999;

/**
 * Counts are whole numbers between `min` and 9999.
 *
 * `short` is used in error messages — the on-screen labels are full sentences
 * from the reporting format and would make unreadable validation errors.
 *
 * The transform matters: an empty number input yields '', which Yup coerces to
 * NaN and reports as "must be a number". Mapping '' to undefined lets the
 * `required` message show instead.
 */
const count = (short, { min = 0 } = {}) =>
  Yup.number()
    .transform((value, original) =>
      original === '' || original === null || original === undefined ? undefined : value
    )
    .typeError(`${short} must be a number`)
    .integer(`${short} must be a whole number`)
    .min(min, min === 0 ? `${short} cannot be negative` : `${short} must be at least ${min}`)
    .max(MAX_COUNT, `${short} cannot exceed ${MAX_COUNT}`)
    .required(`${short} is required`);

// Indicators 3-8, rendered in order. Keeping them as data avoids repeating the
// same Grid/Field block a dozen times in the view.
export const COUNT_FIELDS = [
  {
    name: 'number_of_tiranga_exhibitions',
    short: 'Tiranga Exhibitions',
    label:
      'Total number of Tiranga Exhibitions displayed at the offices and public places under the administrative control of Urban Development Dept.',
  },
  {
    name: 'number_of_landmarks_illuminated',
    short: 'Landmarks illuminated',
    label:
      'Total number of prominent Smart City landmarks, Government buildings and public places illuminated in Tiranga colours',
  },
  {
    name: 'number_of_rrr_centres_mobilized',
    short: 'RRR Centres mobilised',
    label: 'Total number of RRR Centres mobilised during the campaign',
  },
  {
    name: 'number_of_shg_women_participated',
    short: 'SHG women participated',
    label:
      'Total number of women from Self-Help Groups (SHGs) who participated in Tiranga creation activities',
  },
  {
    name: 'number_of_shgs_engaged',
    short: 'Self-Help Groups engaged',
    label: 'Stakeholders engaged — Self-Help Groups (SHGs)',
  },
  {
    name: 'number_of_safaimitras_engaged',
    short: 'SafaiMitras engaged',
    label: 'Stakeholders engaged — SafaiMitras',
  },
  {
    name: 'number_of_youth_groups_engaged',
    short: 'Youth Groups engaged',
    label: 'Stakeholders engaged — Youth Groups',
  },
  {
    name: 'number_of_bwgs_engaged',
    short: 'Bulk Waste Generators engaged',
    label: 'Stakeholders engaged — Bulk Waste Generators (BWGs)',
  },
  {
    name: 'number_of_post_cleanup_activities',
    short: 'Post-cleanup activities',
    label: 'Total number of post-cleanup activities conducted',
  },
];

export const INITIAL_VALUES = {
  // 1. Yes/No gate
  offices_participation: '',
  // 2. Conditional on the gate being YES
  number_of_offices_participated: '',
  ...Object.fromEntries(COUNT_FIELDS.map((f) => [f.name, ''])),
};

export const HGTHGS2026Schema = Yup.object().shape({
  offices_participation: Yup.string()
    .oneOf(['YES', 'NO'], 'Select Yes or No')
    .required('Please select Yes or No'),

  // Only asked — and only validated — when offices did participate. A "yes"
  // with zero offices contradicts itself, so the minimum here is 1.
  number_of_offices_participated: Yup.mixed().when('offices_participation', {
    is: 'YES',
    then: () => count('Number of offices participated', { min: 1 }),
    otherwise: () => Yup.mixed().notRequired(),
  }),

  ...Object.fromEntries(COUNT_FIELDS.map((f) => [f.name, count(f.short)])),
});

/**
 * Photos live outside Formik (the uploader owns that state), so they are
 * validated separately at submit time. Takes a count rather than an array
 * because the total spans already-saved rows plus newly picked files.
 */
export const validatePhotos = (total = 0) => {
  if (total < MIN_PHOTOS) {
    return `Please upload at least ${MIN_PHOTOS} photograph${MIN_PHOTOS > 1 ? 's' : ''}`;
  }
  if (total > MAX_PHOTOS) {
    return `You can upload a maximum of ${MAX_PHOTOS} photographs`;
  }
  return null;
};
